import React from 'react';
import { QuestionAnswerPayload, QuestionData, QuestionItem } from '../services/aiAssistant';
import { Button, Checkbox, Input, Radio, Tabs, Tag } from 'antd';
import { useMemo, useState } from 'react';

const OTHER_LABEL = 'Other';

interface Props {
  questionData: QuestionData;
  /** 有值 → 只读展示已选项 */
  questionAnswers?: QuestionAnswerPayload;
  /** 非末尾 / 历史消息 → 禁用交互 */
  disabled?: boolean;
  onSubmit: (payload: QuestionAnswerPayload) => void;
}

const hasOther = (q: QuestionItem) => q.options.some((o) => o.label === OTHER_LABEL);

const cardStyle: React.CSSProperties = {
  marginTop: 4,
  padding: '12px 14px',
  background: '#fff',
  border: '1px solid #F0F0F0',
  borderRadius: '14px 14px 14px 4px',
  boxShadow: '0 1px 2px rgba(0,0,0,0.04)'
};

/** 只读快照：蓝色 Tag 展示已选项 */
function ReadonlyView({
  questionData,
  questionAnswers
}: {
  questionData: QuestionData;
  questionAnswers: QuestionAnswerPayload;
}) {
  return (
    <div style={cardStyle}>
      {questionData.questions.map((q, i) => {
        const ans = questionAnswers.answers[i];
        return (
          <div key={i} style={{ marginBottom: i < questionData.questions.length - 1 ? 12 : 0 }}>
            {q.header && <div style={{ fontSize: 12, color: '#8a94a6', marginBottom: 4 }}>{q.header}</div>}
            <div style={{ fontSize: 14, color: '#262626', marginBottom: 6 }}>{q.question}</div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {(ans?.selected ?? []).map((label, k) => (
                <Tag key={k} color="blue">
                  {label}
                </Tag>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}

/** 单个子问题的选项渲染 + Other 输入 */
function QuestionBody({
  q,
  selected,
  otherText,
  disabled,
  onChange,
  onOtherChange,
  onSingleSelect
}: {
  q: QuestionItem;
  selected: string[];
  otherText: string;
  disabled?: boolean;
  onChange: (next: string[]) => void;
  onOtherChange: (text: string) => void;
  /** 单题单选：点选即触发提交 */
  onSingleSelect?: (label: string) => void;
}) {
  const otherEnabled = hasOther(q);
  const showOtherInput = otherEnabled && selected.includes(OTHER_LABEL);

  return (
    <div>
      {q.header && <div style={{ fontSize: 12, color: '#8a94a6', marginBottom: 4 }}>{q.header}</div>}
      <div style={{ fontSize: 14, color: '#262626', marginBottom: 8 }}>{q.question}</div>
      {q.multiSelect ? (
        <Checkbox.Group
          value={selected}
          disabled={disabled}
          onChange={(vals) => onChange(vals as string[])}
          style={{ display: 'flex', flexDirection: 'column', gap: 6 }}
        >
          {q.options.map((o) => (
            <Checkbox key={o.label} value={o.label}>
              {o.label}
              {o.description && <span style={{ color: '#8a94a6', marginLeft: 6, fontSize: 12 }}>{o.description}</span>}
            </Checkbox>
          ))}
        </Checkbox.Group>
      ) : (
        <Radio.Group
          value={selected[0]}
          disabled={disabled}
          onChange={(e) => {
            const label = e.target.value as string;
            if (onSingleSelect) onSingleSelect(label);
            else onChange([label]);
          }}
          style={{ display: 'flex', flexDirection: 'column', gap: 6 }}
        >
          {q.options.map((o) => (
            <Radio key={o.label} value={o.label}>
              {o.label}
              {o.description && <span style={{ color: '#8a94a6', marginLeft: 6, fontSize: 12 }}>{o.description}</span>}
            </Radio>
          ))}
        </Radio.Group>
      )}
      {showOtherInput && (
        <Input
          placeholder="请输入"
          value={otherText}
          disabled={disabled}
          onChange={(e) => onOtherChange(e.target.value)}
          style={{ marginTop: 8 }}
        />
      )}
    </div>
  );
}

export default function QuestionCard({ questionData, questionAnswers, disabled, onSubmit }: Props) {
  const { questions } = questionData;

  const [selectedMap, setSelectedMap] = useState<string[][]>(() => questions.map(() => []));
  const [otherMap, setOtherMap] = useState<string[]>(() => questions.map(() => ''));
  const [activeTab, setActiveTab] = useState('0');

  // 单题单选（无 Other）→ 点选即提交，无确认按钮
  const isSingleQuickSelect = questions.length === 1 && !questions[0].multiSelect && !hasOther(questions[0]);

  const buildPayload = (map: string[][]): QuestionAnswerPayload => ({
    questionId: questionData.questionId,
    answers: questions.map((q, i) => {
      const selected = map[i].map((label) => (label === OTHER_LABEL ? otherMap[i].trim() : label)).filter(Boolean);
      return { header: q.header, question: q.question, selected };
    })
  });

  const canSubmit = useMemo(
    () => selectedMap.every((s, i) => s.length > 0 && (!s.includes(OTHER_LABEL) || otherMap[i].trim())),
    [selectedMap, otherMap]
  );

  if (questionAnswers) {
    return <ReadonlyView questionData={questionData} questionAnswers={questionAnswers} />;
  }

  const setSelected = (i: number, next: string[]) => {
    setSelectedMap((prev) => prev.map((s, idx) => (idx === i ? next : s)));
  };
  const setOther = (i: number, text: string) => {
    setOtherMap((prev) => prev.map((s, idx) => (idx === i ? text : s)));
  };

  const handleQuickSelect = (label: string) => {
    if (disabled) return;
    onSubmit(buildPayload([[label]]));
  };

  return (
    <div style={cardStyle}>
      {questions.length === 1 ? (
        <QuestionBody
          q={questions[0]}
          selected={selectedMap[0]}
          otherText={otherMap[0]}
          disabled={disabled}
          onChange={(next) => setSelected(0, next)}
          onOtherChange={(text) => setOther(0, text)}
          onSingleSelect={isSingleQuickSelect ? handleQuickSelect : undefined}
        />
      ) : (
        <Tabs
          activeKey={activeTab}
          onChange={setActiveTab}
          items={questions.map((q, i) => ({
            key: String(i),
            label: q.header || `问题 ${i + 1}`,
            children: (
              <QuestionBody
                q={q}
                selected={selectedMap[i]}
                otherText={otherMap[i]}
                disabled={disabled}
                onChange={(next) => setSelected(i, next)}
                onOtherChange={(text) => setOther(i, text)}
              />
            )
          }))}
        />
      )}
      {!isSingleQuickSelect && (
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 12 }}>
          <Button
            type="primary"
            size="small"
            disabled={disabled || !canSubmit}
            onClick={() => onSubmit(buildPayload(selectedMap))}
          >
            确认
          </Button>
        </div>
      )}
    </div>
  );
}
